import { Navigate, useMatchRoute } from '@tanstack/react-location';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import { clearAuthSession, setAuthReady, setAuthSession } from '../ReduxSlices/configSlice';
import Spinner from 'react-bootstrap/Spinner';
import { refreshSession } from '../requests/session.request';
import { useAuth } from '../hooks/useAuth';

interface IAuthProviderProps {
  children: React.ReactNode;
}

// Renew the access token this long before it expires.
const REFRESH_AHEAD_MS = 2 * 60 * 1000;

function AuthProvider(props: IAuthProviderProps) {
  const { children } = props;
  const matchRoutes = useMatchRoute();
  const isLoginPage = Boolean(matchRoutes({ to: '/login' }));
  const isChangePasswordPage = Boolean(matchRoutes({ to: '/change-password' }));
  const isForgotPasswordPage = Boolean(matchRoutes({ to: '/forgot-password' }));
  const isResetPasswordPage = Boolean(matchRoutes({ to: '/reset-password' }));
  const isPublicPage = isLoginPage || isForgotPasswordPage || isResetPasswordPage;

  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAuth();
  const accessTokenExpiresAt = useAppSelector((state) => state.config.accessTokenExpiresAt);
  const [isInitializing, setIsInitializing] = useState(true);

  // On start-up, renew an expired (or nearly expired) access token before rendering the app.
  useEffect(() => {
    const init = async () => {
      if (isAuthenticated) {
        const expiresAt = Date.parse(accessTokenExpiresAt);
        if (!expiresAt || expiresAt - Date.now() < REFRESH_AHEAD_MS) {
          await refreshSession();
        }
      }
      dispatch(setAuthReady(true));
      setIsInitializing(false);
    };
    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the session alive: refresh shortly before the access token expires.
  useEffect(() => {
    if (!isAuthenticated || !accessTokenExpiresAt) return undefined;
    const delay = Math.max(Date.parse(accessTokenExpiresAt) - Date.now() - REFRESH_AHEAD_MS, 0);
    const timer = window.setTimeout(() => void refreshSession(), delay);
    return () => window.clearTimeout(timer);
  }, [isAuthenticated, accessTokenExpiresAt]);

  // Keep tabs in sync: refresh tokens are single use, so a rotation in one tab must reach the others.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== 'persist:root' || !event.newValue) return;
      try {
        const root = JSON.parse(event.newValue);
        const config = root?.config ? JSON.parse(root.config) : null;
        if (!config) return;
        if (config.accessToken && config.refreshToken && config.authUser) {
          dispatch(
            setAuthSession({
              accessToken: config.accessToken,
              accessTokenExpiresAt: config.accessTokenExpiresAt,
              refreshToken: config.refreshToken,
              refreshTokenExpiresAt: '',
              user: config.authUser
            })
          );
        } else if (!config.accessToken) {
          dispatch(clearAuthSession());
        }
      } catch {
        // Ignore unparseable storage writes.
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch]);

  if (isInitializing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!isAuthenticated && !isPublicPage) {
    return <Navigate to="/login" replace />;
  }

  // A temporary password must be replaced before anything else (the token has no scopes yet).
  if (isAuthenticated && user?.mustChangePassword && !isChangePasswordPage) {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

export default AuthProvider;
